import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { Player, Scout, User, Club, ClubStaff, BlogPost, Competition, CompetitionFixture } from '../types'
import { apiClient } from '../utils/api'
import { APPROVED_COMPETITION_EXAMPLES } from '../utils/competitions'

type UpdatePlayerOptions = { useServiceRole?: boolean }

interface PlayerStore {
  players: Player[]
  setPlayers: (players: Player[]) => void
  addPlayer: (player: Player) => Promise<void>
  updatePlayer: (id: string, player: Partial<Player>, options?: UpdatePlayerOptions) => Promise<void>
  bulkUpdatePlayers: (ids: string[], updates: Partial<Player>, options?: UpdatePlayerOptions) => Promise<{ updated: number; failedIds: string[] }>
  deletePlayer: (id: string, options?: UpdatePlayerOptions) => Promise<void>
  getPlayer: (id: string) => Player | undefined
  addMatchRecord: (playerId: string, record: any) => void
  updateScoutReport: (playerId: string, report: string, author?: string, saved_at?: string) => Promise<void>
  addPlayers: (players: Player[]) => Promise<void>
  fetchPlayers: () => Promise<void>
  clubs: Club[]
  addClub: (club: Club) => Promise<void>
  updateClub: (id: string, club: Partial<Club>) => Promise<void>
  deleteClub: (id: string) => Promise<void>
  fetchClubs: () => Promise<void>
  staff: ClubStaff[]
  staffLoading: boolean
  fetchStaff: (clubId: string) => Promise<void>
  addStaff: (staff: Omit<ClubStaff, 'id' | 'created_at' | 'updated_at'>) => Promise<void>
  updateStaff: (id: string, updates: Partial<ClubStaff>) => Promise<void>
  deleteStaff: (id: string) => Promise<void>
  competitions: Competition[]
  addCompetition: (competition: Competition) => Promise<void>
  updateCompetition: (id: string, competition: Partial<Competition>) => Promise<void>
  deleteCompetition: (id: string) => Promise<void>
  competitionFixtures: CompetitionFixture[]
  setCompetitions: (competitions: Competition[]) => void
  setCompetitionFixtures: (fixtures: CompetitionFixture[]) => void
  fetchCompetitions: () => Promise<void>
  fetchCompetitionFixtures: (competitionId?: string) => Promise<void>
}

interface ScoutStore {
  scouts: Scout[]
  setScouts: (scouts: Scout[]) => void
  verifyScout: (name: string) => void
  getScout: (name: string) => Scout | undefined
}

interface AuthStore {
  user: User | null
  setUser: (user: User | null) => void
  logout: () => Promise<void>
  checkSession: () => Promise<void>
  isRecoveringPassword: boolean
  clearRecoveryState: () => void
}

interface ThemeStore {
  isDark: boolean
  toggleTheme: () => void
}

// @ts-ignore
export const usePlayerStore = create<PlayerStore>()(
  (set, get) => ({
    players: [
      {
        id: 'sample_1',
        name: 'Victor Osimhen',
        age: 25,
        positions: ['CF'],
        main_position: 'CF',
        club: 'Napoli',
        league: 'Serie A',
        state: 'Lagos',
        lga: 'Lagos',
        goals: 26,
        assists: 4,
        apps: 32,
        shots: 100,
        kp: 20,
        dr: 30,
        du: 40,
        mins: 2500,
        potential: 5,
        technical: 9,
        physical: 10,
        mental: 9,
        notes: 'Elite striker.',
        match_desc: 'Hattrick against Sassuolo.',
        date: '2024-03-10',
        scout: 'System',
        photo_url: null,
        youtube: null,
        is_verified: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        id: 'sample_2',
        name: 'Wilfred Ndidi',
        age: 27,
        positions: ['CDM', 'CM'],
        main_position: 'CDM',
        club: 'Leicester City',
        league: 'Championship',
        state: 'Lagos',
        lga: 'Lagos',
        goals: 1,
        assists: 3,
        apps: 30,
        shots: 10,
        kp: 15,
        dr: 5,
        du: 80,
        mins: 2600,
        potential: 4,
        technical: 7,
        physical: 9,
        mental: 9,
        notes: 'Defensive wall.',
        match_desc: 'Clean tackle in derby.',
        date: '2024-03-15',
        scout: 'System',
        photo_url: null,
        youtube: null,
        is_verified: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        id: 'sample_3',
        name: 'Alex Iwobi',
        age: 27,
        positions: ['CAM', 'CM'],
        main_position: 'CAM',
        club: 'Fulham',
        league: 'Premier League',
        state: 'Lagos',
        lga: 'Lagos',
        goals: 5,
        assists: 8,
        apps: 35,
        shots: 20,
        kp: 40,
        dr: 50,
        du: 10,
        mins: 2800,
        potential: 4,
        technical: 8,
        physical: 7,
        mental: 8,
        notes: 'Versatile playmaker.',
        match_desc: 'Key assist in London derby.',
        date: '2024-03-20',
        scout: 'System',
        photo_url: null,
        youtube: null,
        is_verified: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    ],
    setPlayers: (players) => set({ players }),
    addPlayer: async (player) => {
      try {
        const newPlayer = await apiClient.createPlayer(player)
        if (newPlayer) {
          set((state) => ({ players: [{ ...player, ...newPlayer }, ...state.players] }))
        } else {
          // Fallback if representation return fails but record was likely created
          set((state) => ({ players: [player, ...state.players] }))
        }
      } catch (error: any) {
        console.error('Failed to sync player:', error)
        throw error // Propagate error to UI
      }
    },
    updatePlayer: async (id, updates, options) => {
      try {
        const updatedPlayer = await apiClient.updatePlayer(id, updates, options)
        set((state) => ({
          players: state.players.map((p) => p.id === id ? { ...p, ...updates, ...updatedPlayer } : p)
        }))
      } catch (error) {
        console.error('Failed to sync update:', error)
        throw error
      }
    },
    bulkUpdatePlayers: async (ids, updates, options) => {
      const uniqueIds = [...new Set(ids)].filter(Boolean)

      // When service role is required (e.g. visibility toggles), route through
      // the admin API endpoint which uses the Supabase service role key on the
      // server side and can bypass RLS — the regular anon client cannot.
      if (options?.useServiceRole && 'is_public' in updates) {
        try {
          const { managePlayerVisibility } = await import('../utils/adminApi')
          const result = await managePlayerVisibility(uniqueIds, updates.is_public as boolean)
          if (!result.ok) {
            throw new Error(result.error || 'Failed to update player visibility')
          }
          // Optimistically update local state
          set((state) => ({
            players: state.players.map((player) =>
              uniqueIds.includes(player.id)
                ? { ...player, ...updates }
                : player
            )
          }))
          return { updated: result.updated ?? uniqueIds.length, failedIds: [] }
        } catch (err: any) {
          throw new Error(err.message || 'Failed to update visibility')
        }
      }

      // Standard path — individual updates via the regular REST client
      const results = await Promise.allSettled(
        uniqueIds.map(async (id) => {
          const updatedPlayer = await apiClient.updatePlayer(id, updates, options)
          return { id, updatedPlayer }
        })
      )

      const successfulUpdates = results
        .map((result, index) => result.status === 'fulfilled' ? { id: uniqueIds[index], payload: result.value.updatedPlayer } : null)
        .filter((item): item is { id: string; payload: any } => Boolean(item))

      const failedIds = results
        .map((result, index) => result.status === 'rejected' ? uniqueIds[index] : null)
        .filter((id): id is string => Boolean(id))

      if (successfulUpdates.length > 0) {
        const updatesById = new Map(successfulUpdates.map((item) => [item.id, item.payload]))
        set((state) => ({
          players: state.players.map((player) =>
            updatesById.has(player.id)
              ? { ...player, ...updates, ...updatesById.get(player.id) }
              : player
          )
        }))
      }

      if (failedIds.length > 0) {
        console.error('Failed to bulk update players:', failedIds)
      }

      return { updated: successfulUpdates.length, failedIds }
    },
    deletePlayer: async (id, options) => {
      try {
        await apiClient.deletePlayer(id, options)
        set((state) => ({ players: state.players.filter((p) => p.id !== id) }))
      } catch (error) {
        console.error('Failed to sync delete:', error)
        throw error
      }
    },
    getPlayer: (id) => get().players.find((p) => p.id === id),
    addMatchRecord: (playerId, record) =>
      set((state) => {
        const nextPlayers = state.players.map((p) => {
          if (p.id !== playerId) return p

          const stats = record?.stats || {}

          const competitionNameFromRecord =
            (record?.competition && String(record.competition).trim()) ||
            (p.competitions?.[0] && String(p.competitions[0]).trim()) ||
            (p.league && String(p.league).trim()) ||
            'Unknown Competition'

          const competitionIdFromRecord = record?.competition_id || undefined

          const existingPerformances = p.competition_performances || []

          const existingPerformanceIndex = existingPerformances.findIndex(
            (perf) => String(perf.competition_name || '').trim() === competitionNameFromRecord
          )

          let nextCompetitionPerformances = existingPerformances

          if (existingPerformanceIndex >= 0) {
            nextCompetitionPerformances = existingPerformances.map((perf, idx) =>
              idx !== existingPerformanceIndex
                ? perf
                : {
                    ...perf,
                    competition_id: competitionIdFromRecord ?? perf.competition_id,
                    minutes: (perf.minutes || 0) + (stats.mins || 0),
                    goals: (perf.goals || 0) + (stats.goals || 0),
                    assists: (perf.assists || 0) + (stats.assists || 0),
                  }
            )
          } else {
            const newPerf = {
              id: crypto.randomUUID(),
              player_id: p.id,
              competition_id: competitionIdFromRecord,
              competition_name: competitionNameFromRecord,
              appearance_date: record?.date ? new Date(record.date).toISOString().split('T')[0] : undefined,
              opponent: record?.opponent,
              venue: record?.location,
              minutes: stats.mins || 0,
              goals: stats.goals || 0,
              assists: stats.assists || 0,
            }

            nextCompetitionPerformances = [newPerf, ...existingPerformances]
          }

          return {
            ...p,
            goals: (p.goals || 0) + (stats.goals || 0),
            assists: (p.assists || 0) + (stats.assists || 0),
            apps: (p.apps || 0) + 1,
            mins: (p.mins || 0) + (stats.mins || 0),
            match_history: [record, ...(p.match_history || [])],
            competition_performances: nextCompetitionPerformances,
            updated_at: new Date().toISOString(),
          }
        })

        const updatedPlayer = nextPlayers.find((player) => player.id === playerId)
        if (updatedPlayer) {
          void apiClient.updatePlayer(playerId, {
            goals: updatedPlayer.goals,
            assists: updatedPlayer.assists,
            apps: updatedPlayer.apps,
            mins: updatedPlayer.mins,
            match_history: updatedPlayer.match_history,
            competition_performances: updatedPlayer.competition_performances,
            updated_at: updatedPlayer.updated_at,
          }).catch((error) => {
            console.error('Failed to sync match record to backend:', error)
          })
        }

        return { players: nextPlayers }
      }),
    updateScoutReport: async (playerId, report, author, saved_at) => {
      // Optimistically update local state
      set((state) => ({
        players: state.players.map((p) =>
          p.id === playerId
            ? { ...p, scout_report: report, scout_report_author: author || p.scout_report_author, scout_report_saved_at: saved_at || p.scout_report_saved_at, updated_at: new Date().toISOString() }
            : p
        ),
      }))

      try {
        const payload: any = { scout_report: report }
        if (typeof author === 'string') payload.scout_report_author = author
        if (typeof saved_at === 'string') payload.scout_report_saved_at = saved_at

        await apiClient.updatePlayer(playerId, payload)
      } catch (error) {
        console.error('Failed to persist scout report to backend:', error)
        // Keep optimistic UI; revert logic could be added here if needed
      }
    },
    addPlayers: async (newPlayers: Player[]) => {
      const errors: string[] = []
      const createdPlayers: Player[] = []

      for (const player of newPlayers) {
        try {
          const created = await apiClient.createPlayer(player)
          createdPlayers.push(created || player)
        } catch (error: any) {
          console.error(`Failed to sync player ${player.name}:`, error)
          errors.push(`${player.name}: ${error.message}`)
        }
      }

      if (createdPlayers.length > 0) {
        set((state) => ({
          players: [...state.players, ...createdPlayers]
        }))
      }

      if (errors.length > 0) {
        throw new Error(errors.join('; '))
      }
    },
    fetchPlayers: async () => {
      try {
        const players = await apiClient.getPlayers()
        if (players && players.length > 0) {
          set({ players })
        } else {
          console.warn('No players returned from API, keeping local fallback roster.')
        }
      } catch (error) {
        console.error('Failed to fetch players:', error)
      }
    },
    clubs: [],
    competitions: APPROVED_COMPETITION_EXAMPLES.map((name, index) => ({
      id: `comp_seed_${index + 1}`,
      name,
      season: '2025/26',
      country: 'Nigeria',
      level:
        name === 'NPFL'
          ? 'Top Flight'
          : name === 'NNL'
            ? 'National League Two'
            : name === 'NLO'
              ? 'National League One'
              : name === 'NWFL'
                ? 'Women League'
                : name === 'N-Youth League'
                  ? 'Youth League'
                  : name === 'State FA Cup'
                    ? 'State Cup'
                    : name === 'TCC'
                      ? 'Cup'
                      : name === 'NextGen Football Competition'
                        ? 'Competition'
                        : 'International Tournament',
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    })),
    addCompetition: async (competition: Competition) => {
      try {
        const createdCompetition = await apiClient.createCompetition(competition)
        set((state) => ({
          competitions: [createdCompetition || competition, ...state.competitions]
        }))
      } catch (error) {
        console.error('Failed to sync competition:', error)
        throw error
      }
    },
    updateCompetition: async (id: string, updates: Partial<Competition>) => {
      try {
        const updatedCompetition = await apiClient.updateCompetition(id, updates)
        set((state) => ({
          competitions: state.competitions.map((competition) =>
            competition.id === id ? { ...competition, ...updates, ...updatedCompetition } : competition
          ),
        }))
      } catch (error) {
        console.error('Failed to sync competition update:', error)
        throw error
      }
    },
    deleteCompetition: async (id: string) => {
      try {
        await apiClient.deleteCompetition(id)
        set((state) => ({
          competitions: state.competitions.filter((competition) => competition.id !== id),
        }))
      } catch (error) {
        console.error('Failed to sync competition delete:', error)
        throw error
      }
    },
    competitionFixtures: [],
    addClub: async (club: Club) => {
      try {
        const createdClub = await apiClient.createClub(club)
        set((state) => ({ clubs: [createdClub || club, ...state.clubs] }))
      } catch (error) {
        console.error('Failed to sync club:', error)
        throw error
      }
    },
    updateClub: async (id, updates) => {
      try {
        const updatedClub = await apiClient.updateClub(id, updates)
        set((state) => ({
          clubs: state.clubs.map((club) => club.id === id ? { ...club, ...updates, ...updatedClub } : club)
        }))
      } catch (error) {
        console.error('Failed to sync club update:', error)
        throw error
      }
    },
    deleteClub: async (id) => {
      try {
        await apiClient.deleteClub(id)
        set((state) => ({ clubs: state.clubs.filter((club) => club.id !== id) }))
      } catch (error) {
        console.error('Failed to sync club delete:', error)
        throw error
      }
    },
    fetchClubs: async () => {
      try {
        const clubs = await apiClient.getClubs()
        if (Array.isArray(clubs)) {
          set({ clubs: clubs.map((club: Club) => ({ is_visible: true, is_verified: false, ...club })) })
        }
      } catch (error) {
        console.error('Failed to fetch clubs:', error)
      }
    },
    staff: [],
    staffLoading: false,
    fetchStaff: async (clubId: string) => {
      set({ staffLoading: true })
      try {
        const staff = await apiClient.getClubStaff(clubId)
        set({ staff: Array.isArray(staff) ? staff : [] })
      } catch (error) {
        console.error('Failed to fetch staff:', error)
        set({ staff: [] })
      } finally {
        set({ staffLoading: false })
      }
    },
    addStaff: async (staffData) => {
      try {
        const created = await apiClient.createStaff(staffData)
        set((state) => ({ staff: [created || staffData, ...state.staff] }))
      } catch (error) {
        console.error('Failed to add staff:', error)
        throw error
      }
    },
    updateStaff: async (id, updates) => {
      try {
        const updated = await apiClient.updateStaff(id, updates)
        set((state) => ({
          staff: state.staff.map((s) => s.id === id ? { ...s, ...updates, ...updated } : s)
        }))
      } catch (error) {
        console.error('Failed to update staff:', error)
        throw error
      }
    },
    deleteStaff: async (id) => {
      try {
        await apiClient.deleteStaff(id)
        set((state) => ({ staff: state.staff.filter((s) => s.id !== id) }))
      } catch (error) {
        console.error('Failed to delete staff:', error)
        throw error
      }
    },
    setCompetitions: (competitions) => set({ competitions }),
    setCompetitionFixtures: (competitionFixtures) => set({ competitionFixtures }),
    fetchCompetitions: async () => {
      try {
        const competitions = await apiClient.getCompetitions()
        if (competitions && competitions.length > 0) {
          set({ competitions })
        } else {
          const defaultCompetitions = get().competitions.filter((competition) => competition.id.startsWith('comp_'))
          const seedKey = 'scoutang:competition-seeded'
          const { backendClient } = await import('../utils/backend')
          const { data: { session } } = await backendClient.auth.getSession()
          if (defaultCompetitions.length > 0 && session?.access_token && typeof window !== 'undefined' && !window.localStorage.getItem(seedKey)) {
            const seeded = await Promise.allSettled(
              defaultCompetitions.map((competition) =>
                apiClient.createCompetition({
                  ...competition,
                  id: crypto.randomUUID(),
                })
              )
            )
            const createdCompetitions = seeded
              .filter((entry): entry is PromiseFulfilledResult<Competition> => entry.status === 'fulfilled')
              .map((entry) => entry.value)
            set({ competitions: createdCompetitions.length > 0 ? createdCompetitions : defaultCompetitions })
            window.localStorage.setItem(seedKey, 'true')
          }
        }
      } catch (error) {
        console.error('Failed to fetch competitions:', error)
      }
    },
    fetchCompetitionFixtures: async (competitionId?: string) => {
      try {
        const fixtures = await apiClient.getCompetitionFixtures(competitionId)
        if (Array.isArray(fixtures)) {
          set({ competitionFixtures: fixtures })
        }
      } catch (error) {
        console.error('Failed to fetch competition fixtures:', error)
      }
    },
  })
)

// @ts-ignore
export const useScoutStore = create<ScoutStore>()(
  persist(
    (set, get) => ({
      scouts: [],
      setScouts: (scouts) => set({ scouts }),
      verifyScout: (name) =>
        set((state) => ({
          scouts: state.scouts.map((s) => s.name === name ? { ...s, verified: true } : s)
        })),
      getScout: (name) => get().scouts.find((s) => s.name === name),
    }),
    { name: 'scout-store' }
  )
)

// @ts-ignore
export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      user: null,
      isRecoveringPassword: false,
      clearRecoveryState: () => set({ isRecoveringPassword: false }),
      setUser: (user) => set({ user }),
      logout: async () => {
        const { auth } = await import('../utils/backend')
        await auth.signOut()
        set({ user: null, isRecoveringPassword: false })
      },
      checkSession: async () => {
        const { auth, backendClient } = await import('../utils/backend')

        // Initial session check
        let session: any = null
        try {
          const { data } = await backendClient.auth.getSession()
          session = data.session
        } catch (error: any) {
          const message = String(error?.message || '')
          if (message.toLowerCase().includes('refresh token') || message.toLowerCase().includes('invalid refresh')) {
            await backendClient.auth.signOut().catch(() => undefined)
            apiClient.setToken(null)
            set({ user: null })
            return
          }
          throw error
        }

        const handleSession = async (session: any) => {
          if (session?.user) {
            const sbUser = session.user
            apiClient.setToken(session.access_token)
            try {
              const profile = await auth.getProfile(sbUser.id)
              // Check if user is suspended
              if ((profile as any)?.suspended) {
                await backendClient.auth.signOut().catch(() => undefined)
                apiClient.setToken(null)
                set({ user: null })
                if (typeof window !== 'undefined') {
                  window.alert('Your account has been suspended. Please contact support.')
                }
                return
              }
              const newUser = {
                id: sbUser.id,
                name: sbUser.user_metadata.full_name || sbUser.email || '',
                email: sbUser.email || '',
                org: sbUser.user_metadata.org || '',
                avatarUrl: sbUser.user_metadata.avatar_url || '',
                role: profile.role || 'scout',
                initials: (sbUser.user_metadata.full_name || 'U').charAt(0).toUpperCase()
              }
              const current = useAuthStore.getState().user
              if (current && current.id === newUser.id && current.name === newUser.name && current.email === newUser.email && current.role === newUser.role && current.org === newUser.org && current.avatarUrl === newUser.avatarUrl) {
                return
              }
              set({ user: newUser })
            } catch (e) {
              console.error('Failed to load profile:', e)
            }
          } else {
            apiClient.setToken(null)
            set({ user: null })
          }
        }

        await handleSession(session)

        // Listen for changes
        backendClient.auth.onAuthStateChange((event, session) => {
          if (event === 'PASSWORD_RECOVERY') {
            set({ isRecoveringPassword: true })
          }
          if (event === 'SIGNED_OUT') {
            apiClient.setToken(null)
            set({ user: null, isRecoveringPassword: false })
            return
          }
          if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
            if (!session) {
              apiClient.setToken(null)
              set({ user: null })
              return
            }
            handleSession(session).catch((e) => {
              console.error('Session handler error:', e)
            })
          }
        })
      },
    }),
    {
      name: 'auth-store',
      partialize: (state) => ({ user: state.user }),
      storage: {
        getItem: (name) => {
          const rememberMe = localStorage.getItem('scouta_remember_me') !== 'false'
          const storage = rememberMe ? window.localStorage : window.sessionStorage
          const value = storage.getItem(name)
          return value ? JSON.parse(value) : null
        },
        setItem: (name, value) => {
          const rememberMe = localStorage.getItem('scouta_remember_me') !== 'false'
          const storage = rememberMe ? window.localStorage : window.sessionStorage
          storage.setItem(name, JSON.stringify(value))
        },
        removeItem: (name) => {
          window.localStorage.removeItem(name)
          window.sessionStorage.removeItem(name)
        },
      },
    }
  )
)

export const useThemeStore = create<ThemeStore>()(
  persist(
    (_set) => ({
      isDark: true,
      toggleTheme: () => { } // Forced dark mode
    }),
    { name: 'scoutang-theme' }
  )
)

interface AnnouncementStore {
  announcements: any[]
  setAnnouncements: (announcements: any[]) => void
  addAnnouncement: (announcement: any) => Promise<void>
  updateAnnouncement: (id: string, updates: any) => Promise<void>
  deleteAnnouncement: (id: string) => Promise<void>
  fetchAnnouncements: () => Promise<void>
}

interface BlogStore {
  posts: BlogPost[]
  setPosts: (posts: BlogPost[]) => void
  createBlogPost: (post: Partial<BlogPost>) => Promise<BlogPost>
  updateBlogPost: (id: string, updates: Partial<BlogPost>) => Promise<void>
  deleteBlogPost: (id: string) => Promise<void>
  fetchBlogPosts: () => Promise<void>
}

// @ts-ignore
export const useBlogStore = create<BlogStore>()(
  persist(
    (set, get) => ({
        posts: [
          {
            id: 'blog_sample_1',
            title: 'Welcome to the Scouta Blog',
            summary: 'Stay updated with league fixtures, scouting insights, and talent stories from across Nigeria.',
            content: 'Scouta now publishes public articles, fixture rundowns, and talent updates for scouts, clubs, and football fans across the country.',
            author: 'Scouta Team',
            published_at: new Date().toISOString(),
            tags: ['news', 'launch'],
            is_published: true,
          },
        ],
        setPosts: (posts) => set({ posts }),
        createBlogPost: async (post) => {
          const payload = {
            title: post.title || 'Untitled Blog Post',
            summary: post.summary || '',
            content: post.content || '',
            author: post.author || 'Scouta Team',
            published_at: post.published_at || new Date().toISOString(),
            tags: post.tags || [],
            is_published: post.is_published ?? true,
            cover_image_url: post.cover_image_url || '',
          }
          const created = await apiClient.createBlogPost(payload)
          if (!created?.id) throw new Error('Supabase did not return the saved blog post')
          const newPost: BlogPost = { id: created.id, ...payload }
          set((state) => ({ posts: [newPost, ...state.posts.filter((item) => item.id !== newPost.id)] }))
          return newPost
        },
        updateBlogPost: async (id, updates) => {
          const updated = await apiClient.updateBlogPost(id, updates)
          if (!updated?.id) throw new Error('Blog post was not updated in Supabase')
          set((state) => ({ posts: state.posts.map((post) => post.id === id ? { ...post, ...updates, cover_image_url: updates.cover_image_url ?? post.cover_image_url } : post) }))
        },
        deleteBlogPost: async (id) => {
          await apiClient.deleteBlogPost(id)
          set((state) => ({ posts: state.posts.filter((post) => post.id !== id) }))
        },
        fetchBlogPosts: async () => {
          try {
            const remotePosts = await apiClient.getBlogPosts() as BlogPost[]
            const remoteIds = new Set(remotePosts.map((post) => post.id))
            const cachedOnlyPosts = get().posts.filter((post) =>
              post.id !== 'blog_sample_1'
              && !remoteIds.has(post.id)
              && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(post.id)
            )

            if (cachedOnlyPosts.length > 0) {
              try {
                const syncedPosts = await apiClient.syncBlogPosts(cachedOnlyPosts) as BlogPost[]
                const syncedIds = new Set(syncedPosts.map((post) => post.id))
                const stillLocal = cachedOnlyPosts.filter((post) => !syncedIds.has(post.id))
                set({ posts: [...remotePosts, ...syncedPosts, ...stillLocal] })
                return
              } catch (error) {
                console.error('Failed to sync locally saved blog posts:', error)
              }
            }

            set({ posts: [...remotePosts, ...cachedOnlyPosts] })
          } catch (error) {
            console.error('Failed to fetch blog posts:', error)
          }
        },
      }),
    {
      name: 'scoutang-blog-store',
      partialize: (state) => ({ posts: state.posts }),
    }
  )
)

interface WatchlistStore {
  watchlist: string[]
  addToWatchlist: (playerId: string) => void
  removeFromWatchlist: (playerId: string) => void
  isInWatchlist: (playerId: string) => boolean
  clearWatchlist: () => void
}

// @ts-ignore
export const useWatchlistStore = create<WatchlistStore>()(
  persist(
    (set, get) => ({
      watchlist: [],
      addToWatchlist: (playerId) =>
        set((state) => ({
          watchlist: state.watchlist.includes(playerId)
            ? state.watchlist
            : [...state.watchlist, playerId],
        })),
      removeFromWatchlist: (playerId) =>
        set((state) => ({
          watchlist: state.watchlist.filter((id) => id !== playerId),
        })),
      isInWatchlist: (playerId) => get().watchlist.includes(playerId),
      clearWatchlist: () => set({ watchlist: [] }),
    }),
    { name: 'watchlist-store' }
  )
)

// @ts-ignore
export const useAnnouncementStore = create<AnnouncementStore>()(
  persist(
    (set) => ({
      announcements: [],
      setAnnouncements: (announcements) => set({ announcements }),
      addAnnouncement: async (announcement) => {
        try {
          const newAnnouncement = await apiClient.createAnnouncement(announcement)
          set((state) => ({ announcements: [newAnnouncement, ...state.announcements] }))
        } catch (error) {
          console.error('Failed to create announcement:', error)
          throw error
        }
      },
      updateAnnouncement: async (id, updates) => {
        try {
          await apiClient.updateAnnouncement(id, updates)
          set((state) => ({
            announcements: state.announcements.map((a) => a.id === id ? { ...a, ...updates } : a)
          }))
        } catch (error) {
          console.error('Failed to update announcement:', error)
        }
      },
      deleteAnnouncement: async (id) => {
        try {
          await apiClient.deleteAnnouncement(id)
          set((state) => ({ announcements: state.announcements.filter((a) => a.id !== id) }))
        } catch (error) {
          console.error('Failed to delete announcement:', error)
        }
      },
      fetchAnnouncements: async () => {
        try {
          const announcements = await apiClient.getAnnouncements()
          if (Array.isArray(announcements)) {
            set({ announcements })
          }
        } catch (error) {
          console.error('Failed to fetch announcements:', error)
        }
      }
    }),
    { name: 'announcement-store' }
  )
)
